import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    getClientTransactionUrl,
    getCurrencySymbol,
    removeCommas,
    toFixedTrunc,
    useJunoCommonFunctions,
    validateAmount,
} from '../../../../client/Juno/helpers';
import { apiService } from '../../../../client/common/apiCallService';
import TransactionStatus from '../../TransactionStatus';
import FxCurrencyForm from './FxCurrencyForm';
import FxModal from './FxModal';
import { setLoading } from '../../../../client/Redux/actions';
import JunoButton from '../../common/JunoButton';

export const FxTransaction = ({ open: propOpen, onClose: onCloseModal, row: selectedCurrency }) => {
    const [open, setOpen] = useState(false);
    const { useWindowWidth } = useJunoCommonFunctions();

    const windowWidth = useWindowWidth();

    useEffect(() => {
        propOpen ? setOpen(true) : setOpen(false);
    }, [propOpen]);

    const apiUrl = useSelector(state => state.config.api_url);
    const clientData = useSelector(state => state.juno?.clientData);
    const { getBalances, getTransactions, fetchTransactionOverview, getFeeForTransaction } =
        useJunoCommonFunctions();

    const { balancesCurrencies } = useSelector(state => state.juno);
    const balancesData = useSelector(state => state.juno.balancesCurrencies);

    // Core states
    const [formData, setFormData] = useState({
        fromFiat: null,
        toFiat: null,
        fromAmount: '',
        toAmount: '',
    });
    const [fieldError, setFieldError] = useState({
        fromFiat: '',
        toFiat: '',
        fromAmount: '',
        toAmount: '',
    });

    // Exchange state management
    const [isConfirmModal, setIsConfirmModal] = useState(false);
    const [exchangeRate, setExchangeRate] = useState(null);
    const [fxRate, setFxRate] = useState(null);
    const [fxFee, setFxFee] = useState(0);
    const [loadingRate, setLoadingRate] = useState(false);

    // Status modals
    const [successModal, setSuccessModal] = useState(false);
    const [failedModal, setFailedModal] = useState({ open: false, msg: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    // Initialize with selected currency if provided
    useEffect(() => {
        if (balancesCurrencies?.length >= 2 && !formData.fromFiat && !formData.toFiat) {
            const isSelectedCurrencyValid =
                selectedCurrency && Object.keys(selectedCurrency).length > 0;

            const fromFiat = isSelectedCurrencyValid ? selectedCurrency : balancesCurrencies[0];

            let toFiat =
                balancesCurrencies.find(
                    currency => currency.currencyShortName !== fromFiat.currencyShortName,
                ) || balancesCurrencies[1];

            setFormData({
                fromFiat,
                toFiat,
                fromAmount: '',
                toAmount: '',
            });
        }
    }, [balancesCurrencies, formData.fromFiat, formData.toFiat, selectedCurrency]);

    const handleClose = action => {
        setOpen(false);
        setIsLoading(false);
        setIsConfirmModal(false);

        if (action) {
            onCloseModal?.();
        }
    };

    const handleCurrencyChange = (field, currency) => {
        const oppositeField = field === 'fromFiat' ? 'toFiat' : 'fromFiat';

        let updatedErrors = { ...fieldError };

        if (formData[oppositeField]?.currencyShortName === currency.currencyShortName) {
            updatedErrors = {
                ...updatedErrors,
                toFiat: field === 'toFiat' ? 'From and to fiat cannot be the same' : '',
                fromFiat: field === 'fromFiat' ? 'From and to fiat cannot be the same' : '',
            };
        } else {
            updatedErrors = {
                ...updatedErrors,
                toFiat: '',
                fromFiat: '',
            };
        }

        setFieldError(updatedErrors);

        const updatedFormData = {
            ...formData,
            [field]: currency,
            fromAmount: '',
            toAmount: '',
        };

        setFormData(updatedFormData);
    };

    // Calculate exchange rate when currencies change
    useEffect(() => {
        if (
            formData.fromFiat?.currencyShortName &&
            formData.toFiat?.currencyShortName &&
            !isConfirmModal
        ) {
            handleExchangeRate();
        }
    }, [formData.fromFiat, formData.toFiat, isConfirmModal]);

    const handleExchangeRate = async () => {
        setFxFee(0);
        setFxRate(0);
        setExchangeRate(null);
        setLoadingRate(true);
        if (!formData.fromFiat?.currencyShortName || !formData.toFiat?.currencyShortName) {
            return;
        }

        const fromCurrencyValue = formData.fromFiat.currencyShortName;
        const toCurrencyValue = formData.toFiat.currencyShortName;
        const exchangeUrl =
            (window.location.hostname === 'localhost' ? apiUrl : '') +
            '/restapi/get-fiat-exchange-rate';

        try {
            apiService(
                exchangeUrl,
                {
                    fromCurrency: fromCurrencyValue,
                    toCurrency: toCurrencyValue,
                },
                async data => {
                    if (data) {
                        const payloadForFee = {
                            clientId: clientData?.clientId,
                            transactionType: 'FX',
                            currencyType: 'fiat',
                            fromCurrency: fromCurrencyValue,
                            toCurrency: toCurrencyValue,
                        };
                        const clientFXFee = await getFeeForTransaction(payloadForFee);
                        const fixedRate = data?.fixedRate;
                        const transactionFee = clientFXFee
                            ? +((Number(fixedRate) * clientFXFee.percentFee) / 100).toFixed(2)
                            : 0;
                        const fxRate = +(fixedRate - transactionFee).toFixed(6);
                        setFxFee(transactionFee);
                        setFxRate(fxRate);
                        setExchangeRate(fixedRate);
                        setLoadingRate(false);
                    }
                },
                err => {
                    console.log(err);
                    setLoadingRate(false);
                },
            );
        } catch (err) {
            console.log(err);
            setLoadingRate(false);
        }
    };

    const handleAmountChange = (field, value) => {
        if (fxRate) {
            let fromAmount, toAmount, amountError;

            if (field === 'toAmount') {
                const fixedRate = 1 / fxRate;
                fromAmount = fixedRate && +(removeCommas(value) * fixedRate).toFixed(2); /// Using both toFixed(2) for rounding and toFixedTrunc() for comma formatting and validation (until toFixedTrunc handles rounding in future).
                fromAmount = toFixedTrunc(fromAmount, 2);
                toAmount = value;
                amountError = validateAmount(
                    removeCommas(fromAmount),
                    0,
                    [formData.fromFiat.currencyShortName],
                    balancesData,
                );
            } else if (field === 'fromAmount') {
                toAmount = fxRate && +(removeCommas(value) * fxRate).toFixed(2);
                toAmount = toFixedTrunc(toAmount, 2);
                fromAmount = value;
                amountError = validateAmount(
                    value,
                    0,
                    [formData.fromFiat.currencyShortName],
                    balancesData,
                );
            }

            // Update form data with new amounts
            setFormData({
                ...formData,
                fromAmount,
                toAmount,
            });

            if (amountError) {
                setFieldError({
                    ...fieldError,
                    fromAmount: amountError,
                });
            } else {
                setFieldError({
                    ...fieldError,
                    fromAmount: '',
                    toAmount: '',
                });
            }
        }
    };

    const handleConfirmationModal = () => {
        const hasErrors = Object.values(fieldError).some(error => error !== '');

        if (formData.fromAmount && formData.toAmount && !hasErrors) {
            setIsConfirmModal(true);
        }
    };

    const resetFormData = () => {
        setFormData({
            fromFiat: balancesCurrencies[0] || null,
            toFiat: balancesCurrencies[1] || null,
            fromAmount: '',
            toAmount: '',
        });
        setFieldError({
            fromFiat: '',
            toFiat: '',
            fromAmount: '',
            toAmount: '',
        });
    };

    const handleSuccessModal = () => {
        setSuccessModal(false);
        resetFormData();
        handleClose();
    };

    const handleFailedModal = () => {
        setFailedModal({ open: false, msg: '' });
        resetFormData();
        handleClose();
    };

    const currencyProps = useMemo(
        () => ({
            formData,
            fieldError,
            handleAmountChange,
            handleCurrencyChange,
            loadingRate,
            fxRate,
            setFormData,
            setFieldError,
        }),
        [
            formData,
            fieldError,
            handleAmountChange,
            handleCurrencyChange,
            loadingRate,
            fxRate,
            setFormData,
            setFieldError,
        ],
    );

    const transformedFormData = useMemo(
        () => ({
            fromFiatId: formData.fromFiat,
            toFiatId: formData.toFiat,
            fromFiatAmount: formData.fromAmount,
            toFiatAmount: formData.toAmount,
        }),
        [formData],
    );

    const submit = async () => {
        setIsLoading(true);
        setSubmitError(null);
        if (!transformedFormData?.fromFiatAmount && !transformedFormData?.toFiatAmount) return;

        const fromCurrencyShortName = transformedFormData.fromFiatId?.currencyShortName;
        const toCurrencyShortName = transformedFormData.toFiatId?.currencyShortName;

        const balance = transformedFormData.fromFiatId;
        const transactionEmail = localStorage.getItem('user_name');

        await apiService(
            getClientTransactionUrl(apiUrl),
            {
                currencyType: 'fiat',
                type: 'FX',
                client: clientData?.clientId,
                transactionEmail,
                balance: {
                    balanceAmount: balance?.balanceAmount || 0,
                },
                transactionDetails: {
                    fromCurrency: fromCurrencyShortName,
                    toCurrency: toCurrencyShortName,
                    fromAmount: +removeCommas(transformedFormData?.fromFiatAmount),
                    toAmount: +removeCommas(transformedFormData?.toFiatAmount),
                    fxrate: fxRate,
                    fxFee: fxFee,
                    exchangeRate: exchangeRate,
                },
            },
            async data => {
                if (data) {
                    // Get updated data
                    getBalances && getBalances();
                    getTransactions && getTransactions();
                    fetchTransactionOverview && fetchTransactionOverview();

                    setSuccessModal(true);
                    handleClose(false);
                }
            },
            err => {
                setIsLoading(false);
                setSubmitError(`Transaction failed: ${err.message || err || 'Unknown error'}`);
            },
        );
    };

    return (
        <>
            {!propOpen && (
                <div className="flex justify-between flex-col h-full">
                    <div className="bg-modal-bg p-3 w-full rounded-lg h-[444px] border border-[#E4E4E7] dark:!border-0 !p-4 bg-quickforms-bg">
                        <FxCurrencyForm {...currencyProps} isForm={true} />
                    </div>
                    <JunoButton
                        dataE2e="review-fx"
                        type="widget"
                        onClick={() => {
                            setOpen(true);
                            handleConfirmationModal();
                        }}
                        disabled={
                            Object.values(fieldError).some(error => error !== '') ||
                            !fxRate ||
                            !formData.fromAmount ||
                            !formData.toAmount ||
                            loadingRate
                        }>
                        Review FX
                    </JunoButton>
                </div>
            )}

            <FxModal
                {...currencyProps}
                fxFee={fxFee}
                fxRate={fxRate}
                isConfirmModal={isConfirmModal}
                setIsConfirmModal={setIsConfirmModal}
                isLoading={isLoading}
                isOpen={open}
                handleConfirmationModal={handleConfirmationModal}
                onClose={handleClose}
                setSubmitError={setSubmitError}
                submitError={submitError}
                submit={submit}
                formData={transformedFormData}
                fieldError={fieldError}
                windowWidth={windowWidth}
            />

            {successModal && (
                <TransactionStatus
                    status="success"
                    amount={formData.fromAmount}
                    currency={formData.fromFiat?.currencyShortName}
                    toAmount={formData.toAmount}
                    toCurrency={formData.toFiat?.currencyShortName}
                    onClick={handleSuccessModal}
                    buttonText="CLOSE"
                    open={true}
                    description={'Has Been Exchanged Successfully'}
                />
            )}
            {failedModal.open && (
                <TransactionStatus
                    status="error"
                    description={failedModal.msg || 'Transaction failed'}
                    onClick={handleFailedModal}
                    buttonText="CLOSE"
                    open={true}
                />
            )}
        </>
    );
};

export default FxTransaction;
